import { type InstallmentDataQueryVariables } from '@packages/gql/generated/shopping/graphql';
import { InstallmentDataDocument } from '@packages/gql/generated/shopping/InstallmentDataDocument';
import { useMemo, useState } from 'react';
import { useQuery } from 'urql';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { InstallmentCalculatorErrorFallback } from '../../utils/InstallmentCalculatorErrorFallback';
import type {
  InstallmentCalulatorLayoutProps,
  InstallmentsCalulatorCalculationValuta,
} from '../../types';
import { Compact } from '../../components/CalculatorLayouts/Compact';
import { Columns } from '../../components/CalculatorLayouts/Columns';
import { Collapsible } from '../../components/CalculatorLayouts/Collapsible';
import { InstallmentCalculatorSuspenseFallback } from '../../utils/InstallmentCalculatorSuspenseFallback';

/* GraphQL */ `
  query InstallmentData(
    $basevalue: Float!
    $paymentpause: Boolean
    $source: String
    $installments: Int
  ) {
    installment(
      basevalue: $basevalue
      paymentpause: $paymentpause
      source: $source
      installments: $installments
    ) {
      customerType
      source
      currency
      valuta
      numberOfInstallments
      numberOfRegularInstallments
      paymentPause
      installmentSurcharge {
        value
        format
      }
      overallPrice {
        value
        format
      }
      firstInstallment {
        value
        format
      }
      singleInstallment {
        value
        format
      }
      yearlyInterestFactor {
        value
        format
      }
      lastInstallment {
        value
        format
      }
      fixedMonthlyInterestFactor {
        value
        format
      }
      theoreticalInstallmentCount
      error
      viableInstallments
      paymentPauseSurcharge {
        value
        format
      }
      monthlyInterestRateFactor {
        value
        format
      }
      yearlyInterestRateFactor {
        value
        format
      }
      supportsPaymentPause
      supportsInstallmentSelection
      type
      minValue
      maxValue
      displayOrder
    }
  }
`;

export type InstallmentCalculatorProps = InstallmentCalulatorLayoutProps & {
  /**
   * the base price as number (Integer) in cents
   * the product data price is an Integer so this value must also be an integer
   * the installment data are float currencies
   */
  basePrice: InstallmentsCalulatorCalculationValuta;
  /**
   * should the base price be editable
   *
   * @default false
   */
  hasEditablePrice?: boolean;
  /**
   * the requesting source page template
   *
   * @default "pdv"
   */
  source?: 'pdv';
};

export type CalculatorGQLVariablesState = Pick<
  InstallmentDataQueryVariables,
  'paymentpause' | 'installments'
> & {
  basevalue: InstallmentCalculatorProps['basePrice'];
};

/**
 * InstallmentCalculator component
 *
 * component to fetch data and select the correct layout variation
 *
 * */
export const InstallmentCalculator = ({
  source = 'pdv',
  hasEditablePrice = false,
  basePrice,
  ...props
}: InstallmentCalculatorProps) => {
  const {
    installmentCalculator: { resetInstallmentsOnPriceChange },
  } = useConfig();
  const [variables, setVariables] = useState<CalculatorGQLVariablesState>({
    // gql expects an integer
    // rest ecpects a float
    // gql resolver converts the integer into a float
    basevalue: basePrice,
    paymentpause: false,
    installments: null,
  });

  const [{ data, error, fetching }] = useQuery({
    query: InstallmentDataDocument,
    variables: { source, ...variables },
    context: useMemo(() => ({ suspense: false }), []),
  });

  const { isMobile } = useDeviceType();

  if (error || data?.installment?.error) {
    return <InstallmentCalculatorErrorFallback />;
  }

  if (!data?.installment) {
    return <InstallmentCalculatorSuspenseFallback calculatorLayout={props.layout} />;
  }

  const changeInstallmentHandler = (value: number) =>
    setVariables((prev) => ({ ...prev, installments: value }));

  const changePaymentPauseHandler = (checked: boolean) =>
    setVariables((prev) => ({ ...prev, paymentpause: checked }));

  const changeBasePriceHandler = (value: number) =>
    setVariables((prev) => ({
      ...prev,
      ...(resetInstallmentsOnPriceChange && {
        installments: null,
      }),
      // gql expects an integer
      basevalue: Math.round(value * 100),
    }));

  const componentProps = {
    calculationResult: data.installment,
    // the whole installment calculator is working with a float currency
    // the basePrice value is an Integer -> so let's convert the value here
    calculationValuta: basePrice / 100,
    isLoading: fetching,
    hasEditablePrice,
    onChangeInstallmentValue: changeInstallmentHandler,
    onChangePaymentPauseValue: changePaymentPauseHandler,
    onChangeBasePriceValue: changeBasePriceHandler,
  };

  if (props.layout === 'Compact' || (props.layout === 'Columns' && isMobile)) {
    return <Compact {...componentProps} />;
  }
  if (props.layout === 'Collapsible') {
    return <Collapsible {...componentProps} paymentPlanCollapsed={props.collapsed} />;
  }
  return <Columns {...componentProps} />;
};
